import React, { useState } from 'react'
import {
  ArrowLeft,
  Save,
  RotateCcw,
  Layers,
  FolderOpen,
  Settings,
  DollarSign,
  Calendar,
  Users,
  CheckSquare,
  Plus,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectTypeMasterService from '../../services/projectTypeMasterService'

const ProjectTypeMasterForm = () => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [activeTab, setActiveTab] = useState('basicInfo')
  const [formData, setFormData] = useState({
    projectTypeCode: '',
    projectTypeName: '',
    projectTypeDescription: '',
    projectTypeCategory: 'Internal',
    projectTypeStatus: 'Active',
    supportedPricingModels: [],
    defaultDuration: 30,
    defaultBudget: 0,
    defaultCurrency: 'INR',
    requiresApproval: false,
    requiresClientSignoff: false,
    defaultResourceRoles: [],
    billingFrequency: 'Monthly',
    hasTemplate: false,
    templateId: ''
  })

  const [loading, setLoading] = useState(false)

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addArrayItem = (field, newItem) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], newItem || '']
    }))
  }

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const validateForm = () => {
    if (!formData.projectTypeCode.trim()) {
      toast.error('Project Type Code is required')
      return false
    }
    if (!/^[A-Z0-9-]+$/.test(formData.projectTypeCode.trim())) {
      toast.error('Project Type Code must contain only uppercase letters, numbers, and hyphens')
      return false
    }
    if (!formData.projectTypeName.trim()) {
      toast.error('Project Type Name is required')
      return false
    }
    if (!formData.projectTypeDescription.trim()) {
      toast.error('Project Type Description is required')
      return false
    }
    if (formData.defaultDuration < 1) {
      toast.error('Default Duration must be at least 1 day')
      return false
    }
    if (formData.defaultBudget < 0) {
      toast.error('Default Budget cannot be negative')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      toast.info('Creating Project Type Master record...')
      const response = await projectTypeMasterService.createProjectTypeMaster({
        projectTypeCode: formData.projectTypeCode.toUpperCase(),
        projectTypeName: formData.projectTypeName,
        projectTypeDescription: formData.projectTypeDescription,
        projectTypeCategory: formData.projectTypeCategory,
        projectTypeStatus: formData.projectTypeStatus,
        supportedPricingModels: formData.supportedPricingModels,
        defaultDuration: formData.defaultDuration,
        defaultBudget: formData.defaultBudget,
        defaultCurrency: formData.defaultCurrency,
        requiresApproval: formData.requiresApproval,
        requiresClientSignoff: formData.requiresClientSignoff,
        defaultResourceRoles: formData.defaultResourceRoles,
        billingFrequency: formData.billingFrequency,
        hasTemplate: formData.hasTemplate,
        templateId: formData.templateId || null
      })

      if (response.success) {
        toast.success('Project Type Master record created successfully!', { autoClose: 2000 })
        navigate('/projects/project-type-master')
      } else {
        toast.error(response.message || 'Failed to create Project Type Master record')
      }
    } catch (error) {
      console.error('Error creating Project Type Master record:', error)
      if (error.response?.status === 400 && error.response.data?.message.includes('already exists')) {
        toast.error('Project Type Code already exists. Please use a different code.')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to perform this action.')
      } else {
        toast.error('Failed to create Project Type Master record. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      projectTypeCode: '',
      projectTypeName: '',
      projectTypeDescription: '',
      projectTypeCategory: 'Internal',
      projectTypeStatus: 'Active',
      supportedPricingModels: [],
      defaultDuration: 30,
      defaultBudget: 0,
      defaultCurrency: 'INR',
      requiresApproval: false,
      requiresClientSignoff: false,
      defaultResourceRoles: [],
      billingFrequency: 'Monthly',
      hasTemplate: false,
      templateId: ''
    })
    toast.info('Form reset to default values')
  }

  const handleBack = () => {
    navigate('/projects/project-type-master')
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Masters', path: '/projects?tab=masters', onClick: () => navigate('/projects?tab=masters') },
    { label: 'Project Type Master', path: '/projects/project-type-master', onClick: () => navigate('/projects/project-type-master') },
    { label: 'Create New', path: '/projects/project-type-master/new', isCurrent: true }
  ]

  const pricingModelOptions = [
    'Time & Material',
    'Fixed Price',
    'Staff Augmentation',
    'Hybrid'
  ]

  const categoryOptions = [
    'Internal',
    'External',
    'Support',
    'Development'
  ]

  const statusOptions = [
    'Active',
    'Inactive',
    'Suspended'
  ]

  const billingFrequencyOptions = [
    'Weekly',
    'Bi-weekly',
    'Monthly',
    'Milestone-based',
    'Project-based'
  ]

  const currencyOptions = [
    'INR',
    'USD',
    'EUR',
    'GBP'
  ]

  const helpContent = (
    <>
      <h3>What is Project Type Master?</h3>
      <p>Project Type Master lets you <strong>define project types</strong> such as Internal, External, Support, and Development. You can set category, status, supported pricing models, default duration/budget, approval and client signoff rules, billing frequency, and optional template.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Info</strong> – Code, name, description, category, status, supported pricing models, default duration, budget, currency, approval/signoff flags, billing frequency, template link.</p>
      <h3>How to use</h3>
      <p>Fill required fields (code, name, description). Code must be uppercase letters, numbers, and hyphens. Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Project Type Master Entry</h1>
          <p>Create and manage project type information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Project Type Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'basicInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('basicInfo')}
          >
            <Layers size={18} />
            <span>Basic Information</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'pricing' ? 'active' : ''}`}
            onClick={() => setActiveTab('pricing')}
          >
            <DollarSign size={18} />
            <span>Pricing & Budget</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'workflow' ? 'active' : ''}`}
            onClick={() => setActiveTab('workflow')}
          >
            <CheckSquare size={18} />
            <span>Workflow & Approval</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'resources' ? 'active' : ''}`}
            onClick={() => setActiveTab('resources')}
          >
            <Users size={18} />
            <span>Resource Roles</span>
          </button>
        </div>
          
        <form className="form-content">

          {/* Basic Information Tab */}
          {activeTab === 'basicInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Basic Information</h3>
              </div>
              
              {/* Row 1: Project Type Code, Project Type Name, Category, Status */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="projectTypeCode" className="form-label required">
                    Project Type Code
                  </label>
                  <input
                    id="projectTypeCode"
                    type="text"
                    value={formData.projectTypeCode}
                    onChange={(e) => handleInputChange('projectTypeCode', e.target.value)}
                    className="form-input"
                    placeholder="e.g., INT-RND, EXT-WEB"
                    maxLength={20}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="projectTypeName" className="form-label required">
                    Project Type Name
                  </label>
                  <input
                    id="projectTypeName"
                    type="text"
                    value={formData.projectTypeName}
                    onChange={(e) => handleInputChange('projectTypeName', e.target.value)}
                    className="form-input"
                    placeholder="e.g., Internal R&D"
                    maxLength={100}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="projectTypeCategory" className="form-label required">
                    Project Category
                  </label>
                  <select
                    id="projectTypeCategory"
                    value={formData.projectTypeCategory}
                    onChange={(e) => handleInputChange('projectTypeCategory', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Category</option>
                    <option value="Internal">Internal</option>
                    <option value="External">External</option>
                    <option value="Support">Support</option>
                    <option value="Development">Development</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="projectTypeStatus" className="form-label">
                    Status
                  </label>
                  <select
                    id="projectTypeStatus"
                    value={formData.projectTypeStatus}
                    onChange={(e) => handleInputChange('projectTypeStatus', e.target.value)}
                    className="form-input"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Project Type Description */}
              <div className="form-group">
                <label htmlFor="projectTypeDescription" className="form-label required">
                  Project Type Description
                </label>
                <textarea
                  id="projectTypeDescription"
                  value={formData.projectTypeDescription}
                  onChange={(e) => handleInputChange('projectTypeDescription', e.target.value)}
                  className="form-input"
                  placeholder="Describe the purpose and scope of this project type..."
                  rows={3}
                  maxLength={500}
                />
              </div>
            </div>
          )}

          {/* Pricing & Budget Tab */}
          {activeTab === 'pricing' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Pricing & Budget Information</h3>
              </div>
              
              {/* Row 1: Default Duration, Default Budget, Currency, Billing Frequency */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="defaultDuration" className="form-label">
                    Default Duration (Days)
                  </label>
                  <input
                    id="defaultDuration"
                    type="number"
                    value={formData.defaultDuration}
                    onChange={(e) => handleInputChange('defaultDuration', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="1"
                    max="3650"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="defaultBudget" className="form-label">
                    Default Budget
                  </label>
                  <input
                    id="defaultBudget"
                    type="number"
                    value={formData.defaultBudget}
                    onChange={(e) => handleInputChange('defaultBudget', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="defaultCurrency" className="form-label">
                    Currency
                  </label>
                  <select
                    id="defaultCurrency"
                    value={formData.defaultCurrency}
                    onChange={(e) => handleInputChange('defaultCurrency', e.target.value)}
                    className="form-input"
                  >
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="billingFrequency" className="form-label">
                    Billing Frequency
                  </label>
                  <select
                    id="billingFrequency"
                    value={formData.billingFrequency}
                    onChange={(e) => handleInputChange('billingFrequency', e.target.value)}
                    className="form-input"
                  >
                    <option value="Weekly">Weekly</option>
                    <option value="Bi-weekly">Bi-weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Milestone-based">Milestone-based</option>
                    <option value="Project-based">Project-based</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Supported Pricing Models */}
              <div className="form-group">
                <label className="form-label">Supported Pricing Models</label>
                <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {['Time & Material', 'Fixed Price', 'Staff Augmentation', 'Hybrid'].map(option => (
                    <label key={option} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={formData.supportedPricingModels.includes(option)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            addArrayItem('supportedPricingModels', option)
                          } else {
                            removeArrayItem('supportedPricingModels', formData.supportedPricingModels.indexOf(option))
                          }
                        }}
                        style={{ margin: 0 }}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Workflow & Approval Tab */}
          {activeTab === 'workflow' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Workflow & Approval Settings</h3>
              </div>
              
              {/* Row 1: Approval Settings */}
              <div className="grid-row-2">
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.requiresApproval}
                      onChange={(e) => handleInputChange('requiresApproval', e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    <span>Requires Internal Approval</span>
                  </label>
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.requiresClientSignoff}
                      onChange={(e) => handleInputChange('requiresClientSignoff', e.target.checked)}
                      style={{ margin: 0 }}
                    />
                    <span>Requires Client Sign-off</span>
                  </label>
                </div>
              </div>

              {/* Row 2: Template Settings */}
              <div className="form-group">
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={formData.hasTemplate}
                    onChange={(e) => handleInputChange('hasTemplate', e.target.checked)}
                    style={{ margin: 0 }}
                  />
                  <span>Has Project Template</span>
                </label>
              </div>
            </div>
          )}

          {/* Resource Roles Tab */}
          {activeTab === 'resources' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Default Resource Roles</h3>
                <p>Define the typical roles required for this project type</p>
              </div>
              
              <div className="form-group">
                <label className="form-label">Resource Roles Configuration</label>
                
                {/* Resource Roles List */}
                <div className="resource-roles-container" style={{ 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '8px', 
                  padding: '16px',
                  backgroundColor: '#f8fafc',
                  minHeight: '200px'
                }}>
                  {formData.defaultResourceRoles.length === 0 ? (
                    <div className="empty-state" style={{ 
                      textAlign: 'center', 
                      padding: '40px 20px',
                      color: '#64748b'
                    }}>
                      <Users size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>No Resource Roles Added</h4>
                      <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>Add resource roles that are typically required for this project type</p>
                      <button
                        type="button"
                        onClick={() => addArrayItem('defaultResourceRoles', '')}
                        className="btn-primary"
                        style={{ fontSize: '14px', padding: '8px 16px' }}
                      >
                        <Plus size={16} style={{ marginRight: '8px' }} />
                        Add First Role
                      </button>
                    </div>
                  ) : (
                    <div className="resource-roles-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {formData.defaultResourceRoles.map((role, index) => (
                        <div key={index} className="resource-role-item" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                        }}>
                          <div className="role-number" style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#292752',
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {index + 1}
                          </div>
                          
                          <input
                            type="text"
                            value={role}
                            onChange={(e) => {
                              const newRoles = [...formData.defaultResourceRoles]
                              newRoles[index] = e.target.value
                              handleArrayChange('defaultResourceRoles', newRoles)
                            }}
                            className="form-input"
                            placeholder="Enter role name (e.g., Project Manager, Developer, Designer)"
                            style={{ 
                              flex: 1,
                              margin: 0,
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              padding: '8px 12px',
                              fontSize: '14px'
                            }}
                          />
                          
                          <button
                            type="button"
                            onClick={() => removeArrayItem('defaultResourceRoles', index)}
                            className="btn-danger-small"
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <span>Remove</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Role Button */}
                  {formData.defaultResourceRoles.length > 0 && (
                    <div style={{ marginTop: '16px', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => addArrayItem('defaultResourceRoles', '')}
                        className="btn-secondary"
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <Plus size={16} />
                        Add Another Role
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Help Text */}
                <div style={{ 
                  marginTop: '8px', 
                  fontSize: '12px', 
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>💡</span>
                  <span>Common roles: Project Manager, Developer, Designer, QA Engineer, DevOps Engineer, Business Analyst</span>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={handleReset}
              disabled={loading}
            >
              <RotateCcw size={18} />
              Reset
            </button>
            
            <button 
              type="button" 
              className="btn-primary" 
              onClick={handleSave}
              disabled={loading}
            >
              <Save size={18} />
              {loading ? 'Creating...' : 'Create Project Type'}
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Project Type Master – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ProjectTypeMasterForm
